import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _object_spread_props from "@swc/helpers/src/_object_spread_props.mjs";
import React, { useContext, useMemo } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import ddt from '@wix/da-ddt';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import EllipsisIcon from '@wix/da-ds/pkg/Icons/Ellipsis';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { BiContext } from '@wix/da-bi/pkg/BiLogger.context';
import { BiEventType, BiDataHook } from '@wix/da-bi/pkg/constants';
import IconButtonWithContextMenu from '../../../Dropdown/Preset/IconButtonWithContextMenu';
import { CommentContext } from '../../CommentContext';
import { isActionable } from '../../utils';
import { useButtonConditionals } from '../CommentFooter/useButtonConditionals';
import { useVerification } from '../../../VerificationPopup/withVerification';
import s from './ActionsMenu.scss';
const ActionsMenu = ({ className , itemClassName , onHide , onUnhide , onReportSpam , onFeature , onUnfeature , onBlockUser , isCommentReduxReady , canPostComment  })=>{
    const { t  } = useTranslation();
    const isMobile = useContext(MobileContext);
    const { is_comment_thread  } = useContext(BiContext);
    const { comment , setEditingState , canShowActionMenu  } = useContext(CommentContext);
    const { isFeatured , isHidden , isOwner , isAuthor , isReportable  } = comment;
    const { showEditButton  } = useButtonConditionals({
        canPostComment
    });
    const { restrictToVerified  } = useVerification();
    const onEdit = restrictToVerified('comment', ()=>setEditingState('editing'));
    const menuItems = useMemo(()=>{
        var _comment_user;
        const baseBiData = {
            evid: BiEventType.COMMENT_ACTION,
            itemid: comment.itemId,
            typeid: comment.typeId,
            commentid: comment.commentId,
            commenterid: (_comment_user = comment.user) === null || _comment_user === void 0 ? void 0 : _comment_user.useridUuid
        };
        const onToggleHide = ()=>{
            if (!comment.isHidden) {
                onHide && onHide(comment, is_comment_thread === 1);
            } else {
                onUnhide && onUnhide(comment);
            }
        };
        return [
            isOwner && isFeatured && {
                label: t(`comment.popover.unfeature${isMobile ? '.mobile' : ''}`),
                dataHook: BiDataHook.COMMENT_FEATURE_BUTTON,
                className: itemClassName,
                disabled: !isCommentReduxReady,
                onClick: onUnfeature && (()=>onUnfeature(comment)),
                biData: _object_spread_props(_object_spread({}, baseBiData), {
                    menu_item_name: 'unfeature'
                })
            },
            isOwner && !isFeatured && {
                label: t(`comment.popover.feature${isMobile ? '.mobile' : ''}`),
                dataHook: BiDataHook.COMMENT_FEATURE_BUTTON,
                className: itemClassName,
                disabled: !isCommentReduxReady,
                onClick: onFeature && (()=>onFeature(comment)),
                biData: _object_spread_props(_object_spread({}, baseBiData), {
                    menu_item_name: 'feature'
                })
            },
            isAuthor && showEditButton && {
                label: t('comment.popover.edit'),
                dataHook: BiDataHook.COMMENT_EDIT_BUTTON,
                className: itemClassName,
                disabled: !isCommentReduxReady,
                onClick: onEdit,
                biData: _object_spread_props(_object_spread({}, baseBiData), {
                    menu_item_name: 'edit'
                })
            },
            (isOwner || isAuthor) && !isHidden && {
                label: t('comment.popover.delete'),
                dataHook: BiDataHook.COMMENT_DELETE_BUTTON,
                className: itemClassName,
                disabled: !isCommentReduxReady,
                onClick: onToggleHide,
                biData: _object_spread_props(_object_spread({}, baseBiData), {
                    menu_item_name: 'delete'
                })
            },
            isOwner && !isAuthor && {
                label: t(`comment.popover.block${isMobile ? '.mobile' : ''}`),
                dataHook: BiDataHook.COMMENT_BLOCK_BUTTON,
                className: itemClassName,
                disabled: !isCommentReduxReady,
                onClick: ()=>// setTimeout allows actionmenu to be hidden before confirm prompt is shown
                    setTimeout(()=>{
                        if (confirm('Add this user to your block list?') && onBlockUser && comment.user) {
                            onBlockUser(comment.user);
                        }
                    }),
                biData: _object_spread_props(_object_spread({}, baseBiData), {
                    menu_item_name: 'block'
                })
            },
            isReportable && !isAuthor && {
                label: t(`comment.popover.report${isMobile ? '.mobile' : ''}`),
                dataHook: BiDataHook.COMMENT_SPAM_BUTTON,
                className: itemClassName,
                disabled: !isCommentReduxReady,
                onClick: ()=>// setTimeout allows actionmenu to be hidden before confirm prompt is shown
                    setTimeout(()=>{
                        ddt.log('comments', 'reporting spam', comment);
                        if (confirm('Report this comment as spam?') && onReportSpam) {
                            onReportSpam(comment);
                        }
                    }, 100),
                biData: _object_spread_props(_object_spread({}, baseBiData), {
                    menu_item_name: 'spam'
                })
            }
        ];
    }, [
        comment,
        isAuthor,
        isCommentReduxReady,
        isFeatured,
        isHidden,
        isMobile,
        isOwner,
        isReportable,
        is_comment_thread,
        itemClassName,
        onBlockUser,
        onFeature,
        onHide,
        onReportSpam,
        onUnfeature,
        onUnhide,
        t
    ]);
    if (!isActionable(comment) || !canShowActionMenu) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(IconButtonWithContextMenu, {
        className: classnames(s['actions-menu'], className),
        placement: isMobile ? 'top-end' : 'top',
        items: menuItems,
        "aria-label": t('comment.a11y.popover.label'),
        usePortal: true
    }, /*#__PURE__*/ React.createElement("div", {
        "data-hook": BiDataHook.COMMENT_ACTION_MENU
    }, /*#__PURE__*/ React.createElement(EllipsisIcon, {
        size: IconSize.MEDIUM,
        disableDefaultColors: true
    })));
};
ActionsMenu.displayName = 'ActionsMenu';
export default ActionsMenu;
